import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useLocale } from '../../context/LocaleContext';

/**
 * @param {object} props
 * @param {string} props.workspaceName
 * @param {(lang: 'en'|'sv') => void | Promise<void>} [props.onLanguageChange] — persist to API when dashboard data is ready
 */
export default function WorkspaceSwitcher({ workspaceName, onLanguageChange }) {
  const { locale, setLocale, t } = useLocale();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pickLanguage = async (next) => {
    const l = next === 'sv' ? 'sv' : 'en';
    setLocale(l);
    setOpen(false);
    if (onLanguageChange) {
      try {
        await onLanguageChange(l);
      } catch (_) {
        /* locale already updated in UI */
      }
    }
  };

  return (
    <div className={`custio-workspace-wrap${open ? ' custio-workspace-wrap--open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="custio-workspace custio-workspace--trigger"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="custio-workspace__logo" aria-hidden>
          C
        </div>
        <div className="custio-workspace__meta">
          <div className="custio-workspace__label">{t('shell.workspaceLabel')}</div>
          <div className="custio-workspace__name">{workspaceName}</div>
        </div>
        <ChevronDown className="custio-workspace__chevron" size={18} strokeWidth={2} aria-hidden />
      </button>

      {open && (
        <div className="custio-workspace__dropdown" role="menu" aria-label={t('shell.language')}>
          <div className="custio-workspace__dropdown-heading">{t('shell.language')}</div>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={locale === 'en'}
            className={`custio-workspace__lang${locale === 'en' ? ' custio-workspace__lang--active' : ''}`}
            onClick={() => pickLanguage('en')}
          >
            <span>{t('shell.langEnglish')}</span>
            {locale === 'en' ? <Check size={16} strokeWidth={2.5} aria-hidden /> : null}
          </button>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={locale === 'sv'}
            className={`custio-workspace__lang${locale === 'sv' ? ' custio-workspace__lang--active' : ''}`}
            onClick={() => pickLanguage('sv')}
          >
            <span>{t('shell.langSwedish')}</span>
            {locale === 'sv' ? <Check size={16} strokeWidth={2.5} aria-hidden /> : null}
          </button>
        </div>
      )}
    </div>
  );
}
